import JsBarcode from 'jsbarcode';
import type { ProductLot } from '../../types/factory';

// Etiquetas de lote y de pallet: 100 × 60 mm, código de barras Code 128 con el código del lote. Se imprimen por el
// navegador (la impresora térmica de etiquetas se elige en el diálogo de impresión, con ese tamaño de papel).

const nf = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 0 });
const nf1 = new Intl.NumberFormat('es-AR', { maximumFractionDigits: 1 });

export const fmtDateTime = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

const esc = (s?: string | number | null) =>
  String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));

function barcodeSvg(code: string): string {
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  JsBarcode(svg, code, { format: 'CODE128', displayValue: false, height: 70, margin: 0, width: 2 });
  svg.setAttribute('preserveAspectRatio', 'none');
  svg.setAttribute('style', 'width:100%;height:17mm');
  return svg.outerHTML;
}

function labelHtml(l: ProductLot, plantName: string): string {
  const pallet = l.kind === 'Pallet';
  const bags = l.bags != null ? ` · ${nf1.format(l.bags)} ${esc(l.bagName || 'bolsa')}s` : '';
  const rows: [string, string][] = pallet
    ? [
        ['Unidades', `${nf.format(l.units)}${bags}`],
        ['Armado', fmtDateTime(l.openedAt)],
        ['Máquina', [l.machineName, l.shift].filter(Boolean).join(' · ') || '—'],
        ['Ubicación', l.locationCode || '—'],
      ]
    : [
        ['Máquina', [l.machineName, l.moldName].filter(Boolean).join(' · ') || '—'],
        ['Mezcla', l.mixBatchCode || '—'],
        ['Peso', `${nf1.format(l.kg)} kg · ${nf.format(l.units)} u.${bags}`],
        ['Cerrado', fmtDateTime(l.closedAt || l.openedAt)],
      ];
  return `
  <section class="label">
    <header><span>${esc(plantName)}</span><span>${pallet ? 'PALLET · PRODUCTO TERMINADO' : l.isTrial ? 'LOTE DE PRUEBA · INSUMO' : 'LOTE · INSUMO'}</span></header>
    <div class="product">${esc(l.productName)}</div>
    <table>${rows.map(([k, v]) => `<tr><th>${esc(k)}</th><td>${esc(v)}</td></tr>`).join('')}</table>
    <div class="code">${barcodeSvg(l.code)}<div class="text">${esc(l.code)}</div></div>
  </section>`;
}

/** Imprime una etiqueta por lote (en un iframe oculto, para que no lo frene el bloqueador de ventanas). */
export function printLotLabels(lots: ProductLot[], plantName = 'Parriplast', copies = 1) {
  const pages = lots.flatMap(l => Array.from({ length: Math.max(1, copies) }, () => labelHtml(l, plantName))).join('');
  const html = `<!doctype html><html><head><meta charset="utf-8"><title>Etiquetas</title><style>
    @page { size: 100mm 60mm; margin: 0; }
    * { box-sizing: border-box; }
    body { margin: 0; font-family: Arial, Helvetica, sans-serif; color: #000; }
    .label { width: 100mm; height: 60mm; padding: 3mm 4mm; page-break-after: always; display: flex; flex-direction: column; overflow: hidden; }
    header { display: flex; justify-content: space-between; font-size: 7pt; font-weight: 700; letter-spacing: .04em; border-bottom: .3mm solid #000; padding-bottom: .8mm; }
    .product { font-size: 12pt; font-weight: 700; margin: 1.2mm 0 .8mm; line-height: 1.1; max-height: 9mm; overflow: hidden; }
    table { border-collapse: collapse; font-size: 8pt; width: 100%; }
    th { text-align: left; font-weight: 400; color: #333; width: 19mm; padding: .2mm 0; vertical-align: top; }
    td { font-weight: 700; padding: .2mm 0; }
    .code { margin-top: auto; text-align: center; }
    .text { font-family: 'Courier New', monospace; font-size: 11pt; font-weight: 700; letter-spacing: .08em; }
  </style></head><body>${pages}</body></html>`;

  const frame = document.createElement('iframe');
  frame.setAttribute('aria-hidden', 'true');
  Object.assign(frame.style, { position: 'fixed', right: '0', bottom: '0', width: '0', height: '0', border: '0' });
  document.body.appendChild(frame);
  const doc = frame.contentWindow!.document;
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    frame.contentWindow!.focus();
    frame.contentWindow!.print();
    setTimeout(() => frame.remove(), 60_000);
  }, 250);
}
